import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadFullDictionary } from '../../shared/dictionary';
import type { GauntletAware } from '../../shared/gauntletStep';
import {
  evaluateProbe,
  probesRemaining as computeRemaining,
  requiredCorrect,
  tallyCorrect,
  type BlackboxPuzzle,
  type Classification,
  type Probe,
} from './types';
import './blackbox.css';

/**
 * Blackbox play surface. Gauntlet-only today — the host owns lives and hints,
 * and a hint here buys one extra probe (matching the iOS "Extra probe" action).
 */
export default function BlackboxPlay({
  puzzle,
  gauntlet,
  onSolved,
}: GauntletAware & {
  puzzle: BlackboxPuzzle;
  onSolved?: () => void;
}) {
  const [probes, setProbes] = useState<Probe[]>([]);
  const [extraProbes, setExtraProbes] = useState(0);
  const [classifications, setClassifications] = useState<Array<Classification | null>>(
    () => puzzle.testWords.map(() => null),
  );
  const [revealed, setRevealed] = useState(false);
  const [input, setInput] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [dict, setDict] = useState<Set<string> | null>(null);

  const solvedRef = useRef(false);
  useEffect(() => {
    loadFullDictionary().then(setDict);
  }, []);

  // A hint grants one extra probe slot; the host has already charged the pool.
  const lastHintSignal = useRef(gauntlet?.hintSignal ?? 0);
  useEffect(() => {
    const signal = gauntlet?.hintSignal ?? 0;
    if (signal > lastHintSignal.current) {
      lastHintSignal.current = signal;
      setExtraProbes((n) => n + 1);
      setNotice('Extra probe granted.');
    }
  }, [gauntlet?.hintSignal]);

  const remaining = useMemo(
    () => computeRemaining(probes, extraProbes),
    [probes, extraProbes],
  );

  const submitProbe = useCallback(() => {
    if (revealed) return;
    const word = input.trim().toUpperCase();
    if (!word) return;

    // Duplicate guard — a word already probed and recognized never re-charges.
    if (probes.some((p) => p.word === word && p.result !== 'notRecognized')) {
      setNotice('Already probed that word.');
      setInput('');
      return;
    }

    const result = evaluateProbe(puzzle, word, (w) => dict?.has(w) ?? false);
    if (result !== 'notRecognized' && remaining === 0) {
      setNotice('No probes left. Lock in your answers.');
      return;
    }

    setProbes((prev) => [{ word, result }, ...prev]);
    setNotice(result === 'notRecognized' ? "Not a word I know — that one's free." : null);
    setInput('');
  }, [revealed, input, probes, puzzle, dict, remaining]);

  const setClassification = useCallback(
    (index: number, value: Classification) => {
      if (revealed) return;
      setClassifications((prev) => {
        const next = [...prev];
        // Tapping the active choice again clears it.
        next[index] = next[index] === value ? null : value;
        return next;
      });
    },
    [revealed],
  );

  const lockIn = useCallback(() => {
    if (revealed) return;
    const total = puzzle.testWords.length;
    const correct = tallyCorrect(puzzle, classifications);
    const needed = requiredCorrect(total);

    // Quest mode below threshold: charge a life but keep answers editable, so
    // the player can adjust and lock in again.
    if (gauntlet && correct < needed) {
      gauntlet.onWrong();
      setNotice(
        `${correct}/${total} correct — you need at least ${needed} to advance. Adjust and lock in again.`,
      );
      return;
    }

    // Unset classifications become skips at lock-in.
    setClassifications((prev) => prev.map((c) => c ?? 'skipped'));
    setRevealed(true);
    setNotice(null);

    if (!solvedRef.current) {
      solvedRef.current = true;
      gauntlet?.onSolved();
      onSolved?.();
    }
  }, [revealed, puzzle, classifications, gauntlet, onSolved]);

  const correctCount = tallyCorrect(puzzle, classifications);

  return (
    <div className="bb-board">
      {/* Evidence: what the rule accepts and rejects. */}
      <div className="bb-evidence">
        <div className="bb-evidence-col">
          <div className="bb-evidence-title is-yes">FOLLOWS THE RULE</div>
          <div className="bb-chips">
            {puzzle.positives.map((w) => (
              <span key={w} className="bb-chip is-yes">
                {w}
              </span>
            ))}
          </div>
        </div>
        <div className="bb-evidence-col">
          <div className="bb-evidence-title is-no">BREAKS THE RULE</div>
          <div className="bb-chips">
            {puzzle.negatives.map((w) => (
              <span key={w} className="bb-chip is-no">
                {w}
              </span>
            ))}
          </div>
        </div>
      </div>

      {!revealed && (
        <div className="bb-probe">
          <div className="bb-probe-head">
            <span className="bb-probe-title">PROBE A WORD</span>
            <span className="bb-probe-left">
              {remaining} PROBE{remaining === 1 ? '' : 'S'} LEFT
            </span>
          </div>
          <form
            className="bb-probe-row"
            onSubmit={(e) => {
              e.preventDefault();
              submitProbe();
            }}
          >
            <input
              className="bb-input"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (notice) setNotice(null);
              }}
              placeholder="Test a word…"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-label="Probe word"
            />
            <button type="submit" className="btn btn-primary" disabled={!input.trim()}>
              Probe
            </button>
          </form>
        </div>
      )}

      {notice && <div className="bb-notice">{notice}</div>}

      {probes.length > 0 && (
        <div className="bb-probe-list">
          {probes.map((p, i) => (
            <div key={`${p.word}-${i}`} className={`bb-probe-item is-${p.result}`}>
              <span className="bb-probe-word">{p.word}</span>
              <span className="bb-probe-result">
                {p.result === 'follows'
                  ? 'FOLLOWS'
                  : p.result === 'breaks'
                  ? 'BREAKS'
                  : 'UNKNOWN'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Classification grid. */}
      <div className="bb-tests">
        <div className="bb-tests-title">
          CLASSIFY ALL {puzzle.testWords.length}
          {!revealed && (
            <span className="bb-tests-count">
              {classifications.filter(Boolean).length}/{puzzle.testWords.length} set
            </span>
          )}
        </div>

        {puzzle.testWords.map((word, i) => {
          const choice = classifications[i];
          const truth = puzzle.testWordAnswers[i];
          const wasRight =
            (choice === 'follows' && truth) || (choice === 'breaks' && !truth);
          return (
            <div
              key={word}
              className={
                'bb-test' +
                (revealed ? (wasRight ? ' is-right' : ' is-wrong') : '')
              }
            >
              <span className="bb-test-word">{word}</span>
              {revealed ? (
                <span className="bb-test-truth">
                  {truth ? 'FOLLOWS' : 'BREAKS'}
                  {wasRight ? ' ✓' : ' ✕'}
                </span>
              ) : (
                <span className="bb-test-toggle">
                  <button
                    type="button"
                    className={'bb-toggle' + (choice === 'follows' ? ' is-on' : '')}
                    onClick={() => setClassification(i, 'follows')}
                    aria-label={`${word} follows the rule`}
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    className={'bb-toggle' + (choice === 'breaks' ? ' is-on' : '')}
                    onClick={() => setClassification(i, 'breaks')}
                    aria-label={`${word} breaks the rule`}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>

      {!revealed ? (
        <button type="button" className="btn btn-primary bb-lock" onClick={lockIn}>
          Lock in answers
        </button>
      ) : (
        <div className="bb-reveal">
          <div className="bb-reveal-label">THE HIDDEN RULE</div>
          <div className="bb-reveal-rule">{puzzle.ruleDisplayText}</div>
          <div className="bb-reveal-score">
            {correctCount} / {puzzle.testWords.length} correct
          </div>
        </div>
      )}
    </div>
  );
}
